import React, { useState, useRef, useEffect } from 'react';

/**
 * LegendCaseFileView - Wraps the iframe HTML preview with case-file navigation.
 * 
 * This is the investigator case file view that wraps the existing iframe srcDoc
 * pattern. Shows clickable section table on left, HTML preview on right.
 * 
 * Section IDs come from existing pipeline keys:
 * - Ronin: engagement_scope, attack_paths, human_vectors, channel_kits, detection_gaps, next_moves
 * - Parley: situation_brief, counterpart_map, leverage_batna, winning_path, talk_tracks, risks_walkaway
 * - Counsel: case_posture, theory_case, opposition_profile, motion_strategy, witness_themes, next_moves
 * - Advance: principal_picture, threat_actors, dark_pretexts, travel_sites, family_targets, protective_posture, next_moves
 * - Triage: incident_scope, threat_brief, response_actions, containment, recovery_path, next_moves
 * - Tier1: HTML h2 sections (Executive Summary, DISC, Elicitation Targets, Pretext N)
 */
export default function LegendCaseFileView({
  htmlContent,
  reportTitle,
  sectionMappings = [],
  className = '',
  onDownloadHtml,
}) {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const iframeRef = useRef(null);

  // Parse sections from HTML if mappings not provided
  const [detectedSections, setDetectedSections] = useState([]);

  useEffect(() => {
    if (!htmlContent || sectionMappings.length > 0) return;

    // Parse HTML to extract section headings
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    const sections = [];
    
    // Look for <section> tags with class patterns
    const sectionElements = doc.querySelectorAll('section[class*="-section"]');
    sectionElements.forEach((section, idx) => {
      const id = section.getAttribute('id') || `section-${idx}`;
      const h2 = section.querySelector('h2');
      const title = h2 ? h2.textContent.trim() : `Section ${idx + 1}`;
      sections.push({ id, title });
    });

    // Fallback: look for h2 headings if no section tags
    if (sections.length === 0) {
      const h2Elements = doc.querySelectorAll('h2');
      h2Elements.forEach((h2, idx) => {
        const title = h2.textContent.trim();
        const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        sections.push({ id: id || `section-${idx}`, title });
      });
    }

    setDetectedSections(sections);
  }, [htmlContent, sectionMappings.length]);

  const sections = sectionMappings.length > 0 ? sectionMappings : detectedSections;

  const scrollToSection = (sectionId, index) => {
    setActiveSectionIndex(index);
    
    // Scroll iframe content to section
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;

    try {
      const iframeDoc = iframe.contentWindow.document;
      const target = iframeDoc.getElementById(sectionId) || iframeDoc.querySelector(`[id="${sectionId}"]`);
      
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback: try to find h2 with matching text
        const h2Elements = iframeDoc.querySelectorAll('h2');
        for (const h2 of h2Elements) {
          if (h2.textContent.trim() === sections[index].title) {
            h2.scrollIntoView({ behavior: 'smooth', block: 'start' });
            break;
          }
        }
      }
    } catch (err) {
      console.warn('Could not scroll iframe:', err);
    }
  };

  if (!htmlContent) {
    return (
      <div className={`flex items-center justify-center h-full min-h-[28rem] text-gray-500 text-sm ${className}`}>
        <p>No report generated yet.</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`}>
      {/* Header */}
      <div className="shrink-0 border-b border-[#3a342c] bg-[#101418] px-4 py-2.5 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#c4a35a]">
            {reportTitle || 'Legend Playbook'}
          </h3>
          {sections.length > 0 && (
            <p className="text-xs text-[#6f675c] mt-0.5">
              {sections.length} section{sections.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {onDownloadHtml && (
          <button
            type="button"
            onClick={onDownloadHtml}
            className="px-3 py-1.5 text-xs border border-[#3a342c] text-[#e8e2d6] rounded-md hover:border-[#c4a35a]/50 transition-colors"
          >
            Download HTML
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 flex">
        {/* Section navigator (if sections detected) */}
        {sections.length > 0 && (
          <div className="w-64 shrink-0 border-r border-[#3a342c] bg-[#0c0f12] overflow-y-auto">
            <div className="p-3">
              <p className="text-xs font-semibold text-[#8A8070] uppercase tracking-[0.16em] mb-2">
                Sections
              </p>
              <div className="space-y-1">
                {sections.map((section, idx) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id, idx)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      activeSectionIndex === idx
                        ? 'bg-[#c4a35a]/20 text-[#c4a35a] border border-[#c4a35a]/40'
                        : 'bg-[#14181d] text-[#9a9184] border border-transparent hover:text-[#e8e2d6] hover:bg-[#1a1f25]'
                    }`}
                  >
                    <div className="font-medium leading-snug">{section.title}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* iframe preview */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <iframe
            ref={iframeRef}
            title={reportTitle || 'Legend Playbook'}
            className="w-full h-full min-h-[28rem] bg-[#0c0f12]"
            srcDoc={htmlContent}
          />
        </div>
      </div>
    </div>
  );
}
